import type { PostEnriched } from '@/lib/db/types';
import { fantasyHoleKey } from '@/lib/scoring/fantasy-card';
import { resolvePostCourseSlug } from '@/lib/scoring/resolve-course-slug';

export type FantasyGrossSource = 'group' | 'round_holes';

export interface FantasyGrossEntry {
  fantasyKey: string;
  strokes: number;
  playedAt: string;
  courseName?: string;
  courseSlug?: string;
  roundId?: string | null;
  postId?: string;
  source?: FantasyGrossSource;
}

/** Prefer lower gross; on a stroke tie, prefer group scores over round_holes. */
export function preferEntry(a: FantasyGrossEntry, b: FantasyGrossEntry): FantasyGrossEntry {
  if (a.strokes !== b.strokes) return a.strokes < b.strokes ? a : b;
  if (a.source === 'group' && b.source !== 'group') return a;
  if (b.source === 'group' && a.source !== 'group') return b;
  return a;
}

/** One gross score per physical hole per round — avoids duplicate posts for the same round. */
export function dedupeFantasyGrossByRound(entries: FantasyGrossEntry[]): FantasyGrossEntry[] {
  const byRoundHole = new Map<string, FantasyGrossEntry>();

  entries.forEach((entry) => {
    const roundKey = entry.roundId || entry.postId || `${entry.playedAt}|${entry.courseSlug || entry.courseName || ''}`;
    const key = `${roundKey}:${entry.fantasyKey}`;
    const prev = byRoundHole.get(key);
    if (!prev) {
      byRoundHole.set(key, entry);
      return;
    }
    byRoundHole.set(key, preferEntry(prev, entry));
  });

  return [...byRoundHole.values()];
}

/** Collapse duplicate hole_num rows within one score list (keep lowest gross). */
export function uniqueScoresByHoleNum(
  scores: Array<{ hole_num: number; strokes: number }>,
): Array<{ hole_num: number; strokes: number }> {
  const byHole = new Map<number, number>();
  scores.forEach((s) => {
    if (!Number.isFinite(s.hole_num) || !Number.isFinite(s.strokes) || s.strokes <= 0) return;
    const prev = byHole.get(s.hole_num);
    if (prev == null || s.strokes < prev) byHole.set(s.hole_num, s.strokes);
  });
  return [...byHole.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([hole_num, strokes]) => ({ hole_num, strokes }));
}

function grossStrokesFromHole(
  grossStrokes: number | null | undefined,
  netStrokes: number | null | undefined,
): number | null {
  // Always prefer stored gross; never substitute net.
  if (grossStrokes == null || grossStrokes <= 0) return null;
  void netStrokes;
  return grossStrokes;
}

/** Collect gross-only hole scores for fantasy card (never net_strokes). */
export function collectFantasyGrossEntries(
  posts: PostEnriched[],
  memberId: string,
): FantasyGrossEntry[] {
  const out: FantasyGrossEntry[] = [];
  const seen = new Set<string>();

  const push = (entry: FantasyGrossEntry) => {
    const id = `${entry.fantasyKey}:${entry.playedAt}:${entry.strokes}:${entry.source}:${entry.roundId}:${entry.postId}`;
    if (seen.has(id)) return;
    seen.add(id);
    out.push(entry);
  };

  posts.forEach((p) => {
    if (p.type !== 'round' || !p.round) return;
    const slug = resolvePostCourseSlug(p);
    if (!slug) return;

    const playedAt = p.round.played_at || p.created_at;
    const courseName = p.round.course?.name || p.metadata?.courseName;
    const roundId = p.round_id || p.round.id || null;
    const base = {
      playedAt,
      courseName,
      courseSlug: slug,
      roundId,
      postId: p.id,
    };

    const byFantasyKey = new Map<string, FantasyGrossEntry>();

    const addHole = (holeNum: number, strokes: number, source: FantasyGrossSource) => {
      if (strokes <= 0) return;
      const key = fantasyHoleKey(slug, holeNum);
      if (!key) {
        if (import.meta.env.DEV) {
          console.warn('[fantasy] unmapped hole', { slug, hole: holeNum, postId: p.id, source });
        }
        return;
      }
      const next: FantasyGrossEntry = { ...base, fantasyKey: key, strokes, source };
      const prev = byFantasyKey.get(key);
      byFantasyKey.set(key, prev ? preferEntry(prev, next) : next);
    };

    const myGroup = p.metadata?.groupScores?.find((g) => g.id === memberId);
    if (myGroup?.scores?.length) {
      uniqueScoresByHoleNum(myGroup.scores).forEach((rh) => {
        addHole(rh.hole_num, rh.strokes, 'group');
      });
    }

    // Author hole cards: fill / confirm with round_holes (gross only). Prefer group on conflict.
    if (p.author_id === memberId) {
      uniqueScoresByHoleNum(
        (p.round.round_holes || [])
          .map((rh) => {
            const strokes = grossStrokesFromHole(rh.strokes, rh.net_strokes);
            return strokes == null ? null : { hole_num: rh.hole_num, strokes };
          })
          .filter((h): h is { hole_num: number; strokes: number } => h != null),
      ).forEach((rh) => {
        addHole(rh.hole_num, rh.strokes, 'round_holes');
      });
    }

    byFantasyKey.forEach((entry) => push(entry));
  });

  return dedupeFantasyGrossByRound(out);
}
